'use client'

import { useEffect, useState } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import { cn, getBrandColors } from '@/lib/utils'
import type { AlternativeType } from '@/app/(app)/fetchers'
import type { App } from '@/payload-types'

type AlternativesTypeProps = {
  alternative: AlternativeType
}

type RingProps = {
  apps?: App[]
  size: number
  radius: number
  duration?: number
}

export default function AlternativesType({ alternative }: AlternativesTypeProps) {
  const app = alternative.app as App
  const alternativeApps = alternative.alternativeApps || []

  const [apps, setApps] = useState<App[]>([])

  useEffect(() => {
    if (alternativeApps.length === 0) {
      setApps([])
      return
    }

    const repeats = Math.ceil(24 / alternativeApps.length)
    const duplicatedApps = Array(repeats).fill(alternativeApps).flat()
    setApps(duplicatedApps.slice(0, 12))
  }, [alternative, alternativeApps])

  if (!apps.length) {
    return null
  }

  return (
    <div className="relative z-10 mt-auto h-[280px] w-full overflow-hidden">
      <AppIcon
        icon={app.icon}
        glyph={app.glyph}
        name={app.name}
        slug={app.slug}
        background={app.brandBackgroundColor}
        size={112}
        className="absolute bottom-5 left-1/2 z-30 -translate-x-1/2 shadow-md"
      />

      <div className="absolute inset-0 left-1/2 flex w-full -translate-x-1/2 items-center justify-center before:absolute before:bottom-0 before:z-40 before:h-10 before:w-full before:bg-gradient-to-t before:from-white before:to-transparent after:absolute after:inset-0 after:top-0 after:z-20 after:h-64 after:w-full after:bg-gradient-to-b after:from-white after:to-transparent">
        <Ring apps={apps} size={700} radius={200} />
        <Ring size={1400} radius={310} duration={60} />
      </div>
    </div>
  )
}

export function Ring({ apps, size, radius, duration = 30 }: RingProps) {
  const offset = 72 / 2
  const center = size / 2
  const total = apps?.length || 18
  const items = apps?.length ? apps : Array(total).fill(null)

  return (
    // `meticulous-ignore`: `animate-spin` is a continuous CSS rotation. Different
    // Meticulous runs catch the ring at different rotational frames, causing
    // false-positive visual diffs on every audit. The ring is decorative — its
    // children (app icons) are computed from the same data on every render, so
    // ignoring the rotation doesn't hide any meaningful content change.
    <div
      data-review="ignore"
      className={cn(
        `meticulous-ignore absolute origin-center animate-spin`,
        apps?.length ? 'top-[-112px] z-30' : 'top-[-460px] z-10',
      )}
      style={
        {
          height: `${size}px`,
          width: `${size}px`,
          '--duration': `${duration}s`,
        } as React.CSSProperties & { '--duration': string }
      }
    >
      {items.map((app: any, index: number) => {
        const angle = (index * (2 * Math.PI)) / total
        const left = Math.cos(angle) * radius + center - offset
        const top = Math.sin(angle) * radius + center - offset

        if (!apps?.length) {
          return (
            <div
              key={index}
              className="absolute h-[72px] w-[72px] rounded-[22px] border border-black/10 bg-white shadow-button"
              style={{
                transform: `translate(${left}px, ${top}px) rotate(${(angle * 180) / Math.PI + 90}deg)`,
              }}
            ></div>
          )
        }

        const { backgroundColor } = getBrandColors(app)

        return (
          <div
            key={index}
            className="absolute"
            style={{
              transform: `translate(${left}px, ${top}px) rotate(${(angle * 180) / Math.PI + 90}deg)`,
            }}
          >
            <AppIcon
              icon={app.icon}
              glyph={app.glyph}
              name={app.name}
              slug={app.slug}
              background={backgroundColor}
              size={72}
              invert={true}
              className="shadow-md"
            />
          </div>
        )
      })}
    </div>
  )
}
